"use client";

import { useState, type ReactNode } from "react";
import type { BabyNameResponse } from "@/lib/api/baby-names";
import { SITE_URL } from "@/lib/schema";
import { NameCard } from "./NameCard";
import { padaSyllableHi } from "./syllables";
import Button from "@ishvaram/ui/Button";
import { BABY_NAMES_COPY, type BabyNamesCopy, type BabyNamesLanguage } from "./copy";

/** The page a shared shortlist links back to — a route, not visible copy. */
const SHARE_PATHS: Record<BabyNamesLanguage, string> = { en: "/baby-names", hi: "/hi/baby-names" };

interface BabyNamesResultProps {
  result: BabyNameResponse;
  resultOffer: ReactNode;
  onReset: () => void;
  language?: BabyNamesLanguage;
}

export function BabyNamesResult({
  result,
  resultOffer,
  onReset,
  language = "en",
}: BabyNamesResultProps) {
  const copy = BABY_NAMES_COPY[language];
  const [shortlist, setShortlist] = useState<Set<string>>(new Set());
  const [resultGender, setResultGender] = useState<string>("all");

  function toggleShortlist(name: string) {
    setShortlist((prev) => {
      const next = new Set(prev);
      if (next.has(name)) {
        next.delete(name);
      } else {
        next.add(name);
      }
      return next;
    });
  }

  const otherPadaNames = result.other_pada_names ?? [];

  function shareOnWhatsApp() {
    const shortlisted = [...result.names, ...otherPadaNames].filter((n) => shortlist.has(n.name));
    if (shortlisted.length === 0) return;

    const nakName =
      (language === "hi" ? result.nakshatra?.name_hi : result.nakshatra?.name_en) ||
      result.nakshatra?.name_en ||
      "Unknown";
    const letters = result.starting_letters
      .map((letter) => (language === "hi" ? padaSyllableHi(result.nakshatra?.id, letter) : letter))
      .join(", ");
    const namesList = shortlisted
      .map(
        (n) =>
          `${language === "hi" ? `${n.name_hi} (${n.name})` : `${n.name} (${n.name_hi})`} - ${n.meaning} [${copy.scoreLabel}: ${n.compatibility_score}/10]`
      )
      .join("\n");

    const msg = encodeURIComponent(
      `${copy.shareMessage(nakName, letters, result.mulank.value, result.bhagyank.value)}\n\n` +
        `${namesList}\n\n` +
        `${copy.shareFooter} ${SITE_URL}${SHARE_PATHS[language]}`
    );
    window.open(`https://wa.me/?text=${msg}`, "_blank");
  }

  const byGender = (entry: BabyNameResponse["names"][number]) =>
    resultGender === "all" || entry.gender === resultGender || entry.gender === "unisex";
  const filteredNames = result.names.filter(byGender);
  const filteredOtherNames = otherPadaNames.filter(byGender);
  const renderCard = (entry: BabyNameResponse["names"][number]) => (
    <NameCard
      key={entry.name + entry.nakshatra}
      entry={entry}
      selected={shortlist.has(entry.name)}
      onToggle={() => toggleShortlist(entry.name)}
      copy={copy}
      hindi={language === "hi"}
    />
  );

  return (
    <div className="space-y-6" data-design-owner="baby-names-result">
      <NakshatraInfo result={result} copy={copy} hi={language === "hi"} />
      {resultOffer}
      <ResultFilterBar
        resultGender={resultGender}
        resultCount={filteredNames.length}
        onGenderChange={setResultGender}
        copy={copy}
      />
      <ShortlistActions shortlistSize={shortlist.size} onShare={shareOnWhatsApp} copy={copy} />

      {/* Names List */}
      <div className="space-y-3">{filteredNames.map(renderCard)}</div>

      {/* Thin pada (< 5 names, e.g. आर्द्रा पद 3 has none): the engine adds the
          same nakshatra's other padas' names, labelled — never invented. */}
      {filteredOtherNames.length > 0 && (
        <section aria-labelledby="baby-names-other-padas" className="space-y-3">
          <h3
            id="baby-names-other-padas"
            className="font-serif text-body font-bold text-text-primary"
          >
            {copy.otherPadasHeading(result.other_padas ?? [])}
          </h3>
          <p className="text-caption text-text-muted">{copy.otherPadasNote}</p>
          {filteredOtherNames.map(renderCard)}
        </section>
      )}

      {filteredNames.length === 0 && filteredOtherNames.length === 0 && (
        <div className="text-center py-8">
          <p className="text-body-small text-text-secondary">{copy.empty}</p>
        </div>
      )}

      {/* Actions */}
      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        <Button
          variant="secondary"
          onPress={onReset}
          title={copy.again}
          className="rounded-lg px-6 py-3 text-center text-button font-semibold"
        />
      </div>
    </div>
  );
}

function NakshatraInfo({
  result,
  copy,
  hi,
}: {
  result: BabyNameResponse;
  copy: BabyNamesCopy;
  hi: boolean;
}) {
  const english = result.nakshatra?.name_en?.replace(/_/g, " ");
  const devanagari = result.nakshatra?.name_hi;
  const [heading, gloss] = hi && devanagari ? [devanagari, english] : [english, devanagari];
  return (
    <div className="card-sacred p-6 sm:p-8 text-center">
      <span className="text-4xl block mb-3">&#x2606;</span>
      <h2 className="font-serif text-title font-bold capitalize text-text-primary">
        {heading || copy.selectedNakshatra}
      </h2>
      {gloss && <p className="mt-1 text-body text-text-secondary">{gloss}</p>}
      <BirthStarLine result={result} copy={copy} />

      <div className="divider-warm my-5" />
      <StartingLetters result={result} copy={copy} hi={hi} />
      <NumerologyInfo result={result} copy={copy} />
    </div>
  );
}

/** "चंद्र राशि: वृषभ · नक्षत्र: रोहिणी, पद 3" — the rashi the engine computed. */
function BirthStarLine({ result, copy }: { result: BabyNameResponse; copy: BabyNamesCopy }) {
  const { nakshatra, moon_rashi: rashi, pada } = result;
  if (!nakshatra && !rashi) return null;
  const parts: string[] = [];
  if (rashi) parts.push(`${copy.moonSign}: ${copy.rashiName(rashi)}`);
  if (nakshatra) {
    const padaText = pada ? `, ${copy.padaShort(pada)}` : "";
    parts.push(`${copy.nakshatraWord}: ${copy.nakshatraName(nakshatra)}${padaText}`);
  }
  return (
    <p
      className="mt-3 text-body-small font-semibold text-text-primary"
      data-testid="birth-star-line"
    >
      {parts.join(" · ")}
    </p>
  );
}

function StartingLetters({
  result,
  copy,
  hi,
}: {
  result: BabyNameResponse;
  copy: BabyNamesCopy;
  hi: boolean;
}) {
  return (
    <div className="mb-4">
      <span className="mb-2 block text-caption text-text-muted">{copy.startingLetters}</span>
      <div className="flex justify-center gap-2 flex-wrap">
        {result.starting_letters.map((letter) => (
          <span
            key={letter}
            className={`rounded-lg border px-3 py-1.5 text-body-small font-bold ${
              result.pada_letter === letter
                ? "border-border-interactive bg-surface-brand-strong text-text-on-brand"
                : "border-border-interactive bg-surface-brand-subtle text-intent-brand"
            }`}
          >
            {hi ? padaSyllableHi(result.nakshatra?.id, letter) : letter}
          </span>
        ))}
      </div>
      {result.pada_letter && (
        <p className="mt-2 text-caption text-text-muted">{copy.padaHighlighted(result.pada)}</p>
      )}
    </div>
  );
}

function NumerologyInfo({ result, copy }: { result: BabyNameResponse; copy: BabyNamesCopy }) {
  return (
    <div className="grid grid-cols-2 gap-4">
      <div>
        <span className="mb-1 block text-caption text-text-muted">{copy.mulank}</span>
        <span className="text-title font-bold text-text-primary">{result.mulank.value}</span>
      </div>
      <div>
        <span className="mb-1 block text-caption text-text-muted">{copy.bhagyank}</span>
        <span className="text-title font-bold text-text-primary">{result.bhagyank.value}</span>
      </div>
    </div>
  );
}

function ResultFilterBar({
  resultGender,
  resultCount,
  onGenderChange,
  copy,
}: {
  resultGender: string;
  resultCount: number;
  onGenderChange: (value: string) => void;
  copy: BabyNamesCopy;
}) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div className="flex gap-1.5">
        {[
          { value: "all", label: copy.genders.all },
          { value: "male", label: copy.genders.male },
          { value: "female", label: copy.genders.female },
        ].map((opt) => (
          <button
            key={opt.value}
            type="button"
            aria-pressed={resultGender === opt.value}
            className={`rounded-lg border px-3 py-1.5 text-caption font-semibold transition-all ${
              resultGender === opt.value
                ? "border-border-interactive bg-surface-brand-strong text-text-on-brand"
                : "border-border-default bg-surface-raised text-text-secondary"
            }`}
            onClick={() => onGenderChange(opt.value)}
          >
            {opt.label}
          </button>
        ))}
      </div>

      <span className="text-caption text-text-muted">{copy.count(resultCount)}</span>
    </div>
  );
}

function ShortlistActions({
  shortlistSize,
  onShare,
  copy,
}: {
  shortlistSize: number;
  onShare: () => void;
  copy: BabyNamesCopy;
}) {
  if (shortlistSize === 0) return null;
  return (
    <div className="card-sacred flex items-center justify-between border-[1.5px] border-border-interactive p-4">
      <span className="text-body-small font-semibold text-text-primary">
        {copy.shortlisted(shortlistSize)}
      </span>
      <button
        type="button"
        onClick={onShare}
        className="flex items-center gap-2 rounded-lg bg-whatsapp px-4 py-2 text-body-small font-semibold text-text-on-brand transition-all"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
        </svg>
        {copy.share}
      </button>
    </div>
  );
}
